import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import moment from "moment";
import {
  SLOT_STEP_MINUTES,
  generateDays,
  generateTimeSlots,
  isOpenNow,
  isSameSlot,
} from "./orderTimeSlots";
import { cateringDeliveryOffered, cateringEarliest } from "../../utils/catering";
import { dayKeyOf, dayTimeIn, restaurantDayOf, restaurantToday, timeIn, zonedWallTimeToUtc } from "../../utils/restaurantClock";
import { C, RADIUS, TYPE } from "../../utils/designTokens";
import { OptionCard, RadioGroup, SelectField, metaSx } from "./checkoutUi";

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

// `day` is the restaurant's calendar day (utils/restaurantClock.js), so
// "Today" is its today, wherever the customer is.
function dayLabel(day, tz) {
  const diff = Math.round((startOfDay(day) - restaurantToday(tz)) / 86400000);
  if (diff === 0) return `Today, ${moment(day).format("MMM D")}`;
  if (diff === 1) return `Tomorrow, ${moment(day).format("MMM D")}`;
  return moment(day).format("ddd, MMM D");
}

/**
 * Step 1's order type and timing: Delivery or Pickup as two cards, the address
 * card between, then ASAP or Schedule (a Date and a Time dropdown), then
 * whatever the step adds below (drop-off, instructions). Every choice writes
 * straight through to the parent's state and, from there, to the header.
 */
function OrderOptions({
  isPickUp,
  setIsPickUp,
  isAsap,
  setIsAsap,
  selectedDate,
  handleDateChange,
  prepTime = 15,
  openingTimes = [],
  isAvailable = true,
  restaurantData,
  highlight = false,
  isSelfDelivery = false,
  deliveryFeeLoaded = false,
  // A catering cart's settings (backend migration 131), or null for a regular
  // cart. Catering is always scheduled at least leadTimeMinutes ahead, and
  // delivery/pickup follow its deliveryMode and allowPickup.
  catering = null,
  // Rendered between the order type and When: the map + address card.
  addressCard = null,
  children,
}) {
  const [showSchedule, setShowSchedule] = useState(!isAsap);
  // Every day, slot and "open now" here is on the restaurant's clock.
  const tz = restaurantData?.timezone || null;
  const [selectedDay, setSelectedDay] = useState(() => restaurantToday(tz));

  const regularPickup = restaurantData?.enablePickup !== false;

  // Does this restaurant deliver at all?
  //
  // Careful: turning on Self Delivery in the admin panel switches enableDelivery
  // OFF (that flag means "RushmyFood's riders deliver"), so checking it alone
  // hides Delivery from every restaurant that runs its own drivers. Same rule
  // the restaurant list uses — see screens/Restaurants/Restaurants.js.
  //
  // A food truck is different again: delivery is opt-in per truck and gated by
  // mobileDeliveryEnabled, since the "zone" is a radius around wherever it is
  // parked right now.
  const regularDelivery = restaurantData?.isMobile
    ? restaurantData?.mobileDeliveryEnabled === true
    : (restaurantData?.enableDelivery !== false || restaurantData?.selfDelivery === true);

  // A catering cart goes by the restaurant's catering settings instead.
  const enablePickup = catering ? catering.allowPickup !== false : regularPickup;
  const enableDelivery = catering ? cateringDeliveryOffered(catering, regularDelivery) : regularDelivery;

  // Never leave a mode selected that the restaurant does not offer — that is
  // how a pickup-only truck once showed Delivery selected AND "Not offered".
  useEffect(() => {
    if (isPickUp && !enablePickup && enableDelivery) setIsPickUp(false);
    else if (!isPickUp && !enableDelivery && enablePickup) setIsPickUp(true);
  }, [enablePickup, enableDelivery, isPickUp, setIsPickUp]);

  // Who brings the order. The server decides this per address when it prices
  // delivery — a restaurant with its own drivers still falls back to our riders
  // for an address outside the zones they cover — so for those restaurants it
  // only means anything once that lookup has answered.
  const deliveredByLabel = deliveryFeeLoaded
    ? isSelfDelivery
      ? `Delivered by ${restaurantData?.name || "the restaurant"}`
      : "Delivered by RushMyFood"
    : restaurantData?.selfDelivery
      ? null
      : "Delivered by RushMyFood";

  // Catering is never ASAP.
  const asapAvailable = !catering && isAvailable && isOpenNow(openingTimes, tz);

  // Closed right now → ASAP is not a real choice, so land on Schedule rather
  // than leaving an impossible option selected.
  useEffect(() => {
    if (!asapAvailable && isAsap) {
      setIsAsap(false);
      setShowSchedule(true);
    }
  }, [asapAvailable, isAsap, setIsAsap]);

  useEffect(() => {
    if (!isAsap) setShowSchedule(true);
  }, [isAsap]);

  // Catering: the earliest wanted time is now + the notice. The clock is
  // re-read every minute so that floor keeps moving while checkout is open.
  const cateringLead = catering ? Math.max(0, Number(catering.leadTimeMinutes) || 0) : 0;
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    if (!catering) return undefined;
    const id = setInterval(() => setClockTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, [catering]);
  const cateringFloor = useMemo(
    () => (catering ? cateringEarliest(cateringLead) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catering, cateringLead, clockTick]
  );

  // Catering offers a week past the notice; days wholly inside the notice
  // have nothing to pick, so they are left off the list.
  const days = useMemo(() => {
    if (!catering) return generateDays(7, tz);
    return generateDays(Math.ceil(cateringLead / 1440) + 7, tz).filter((day) => {
      const end = new Date(zonedWallTimeToUtc(dayKeyOf(day), 23 * 60 + 59, tz).getTime() + 59999);
      return !cateringFloor || end >= cateringFloor;
    });
  }, [catering, cateringLead, cateringFloor, tz]);
  // The restaurant (and so its time zone) can arrive after the first render;
  // keep the Date dropdown on a day it actually lists.
  useEffect(() => {
    if (!days.length) return;
    setSelectedDay((prev) => (days.some((d) => d.getTime() === prev.getTime()) ? prev : days[0]));
  }, [days]);

  // The earliest slot is the kitchen's prep time out for pickup, 30 minutes
  // for delivery; after that, every 15 minutes.
  const leadMinutes = isPickUp ? prepTime : 30;
  const slotsFor = (day) => generateTimeSlots(day, leadMinutes, openingTimes, SLOT_STEP_MINUTES, cateringFloor, tz);
  const timeSlots = useMemo(
    () => slotsFor(selectedDay),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDay, leadMinutes, openingTimes, cateringFloor, tz]
  );

  // A catering order is always scheduled, at least the notice ahead. When the
  // choice is ASAP or too soon (the header's "now", or a time that went stale),
  // move it to the earliest open slot.
  useEffect(() => {
    if (!catering) return;
    const picked = selectedDate ? new Date(selectedDate) : null;
    // Ten minutes of slack, inside the server's fifteen.
    const tooSoon = !picked || picked.getTime() < Date.now() + (cateringLead - 10) * 60000;
    if (!isAsap && !tooSoon) return;
    setShowSchedule(true);
    for (const day of days) {
      const slots = slotsFor(day);
      if (slots.length) {
        setSelectedDay(day);
        setIsAsap(false);
        handleDateChange(slots[0]);
        return;
      }
    }
    if (isAsap) setIsAsap(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catering, cateringLead, cateringFloor, days, isAsap, selectedDate, leadMinutes, openingTimes, handleDateChange, setIsAsap]);

  // Keep the Date dropdown on the chosen time's day, wherever it was chosen
  // (here, the header, or the catering page).
  const selectedTs = selectedDate ? new Date(selectedDate).getTime() : null;
  useEffect(() => {
    if (isAsap || !selectedTs || selectedTs <= Date.now() + 60000) return;
    const day = restaurantDayOf(selectedTs, tz);
    setSelectedDay((prev) => (prev.getTime() === day.getTime() ? prev : day));
  }, [isAsap, selectedTs, tz]);

  const chooseAsap = () => {
    setShowSchedule(false);
    setIsAsap(true);
    handleDateChange(new Date());
  };

  const chooseSlot = (slot) => {
    setIsAsap(false);
    handleDateChange(slot);
  };

  // Scheduling starts on the earliest time that can be ordered for, so a
  // scheduled order always has a time; the customer moves it from there.
  const chooseSchedule = () => {
    setShowSchedule(true);
    setIsAsap(false);
    if (selectedTs && selectedTs > Date.now() + 60000) return;
    for (const day of days) {
      const slots = slotsFor(day);
      if (slots.length) {
        setSelectedDay(day);
        handleDateChange(slots[0]);
        return;
      }
    }
  };

  const chooseDay = (value) => {
    const day = days.find((d) => String(d.getTime()) === value);
    if (!day) return;
    setSelectedDay(day);
    const slots = slotsFor(day);
    if (slots.length) chooseSlot(slots[0]);
  };

  // The Time dropdown shows the chosen time even when it isn't on the grid
  // (one chosen in the header, say), so it never claims a different time.
  const scheduledOnDay = !isAsap && selectedTs && restaurantDayOf(selectedTs, tz).getTime() === selectedDay.getTime();
  const matchingSlot = scheduledOnDay ? timeSlots.find((s) => isSameSlot(s, selectedTs)) : null;
  const offGrid = scheduledOnDay && !matchingSlot && selectedTs > Date.now();
  const timeValue = matchingSlot ? String(matchingSlot.getTime()) : offGrid ? String(selectedTs) : "";

  const scheduleCaption = !isAsap && selectedTs && selectedTs > Date.now()
    ? dayTimeIn(selectedTs, tz)
    : "Pick a date & time";

  return (
    <Box
      sx={{
        borderRadius: RADIUS.menuCard,
        outline: highlight ? `2px solid ${C.red}` : "2px solid transparent",
        outlineOffset: 6,
        transition: "outline-color .3s ease",
      }}
    >
      <RadioGroup label="Order type" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
        <OptionCard
          checked={!isPickUp}
          disabled={!enableDelivery}
          onSelect={() => setIsPickUp(false)}
          icon={<DeliveryDiningOutlinedIcon sx={{ fontSize: 28 }} />}
          title="Delivery"
          caption={enableDelivery ? deliveredByLabel : "Not offered here"}
        />
        <OptionCard
          checked={isPickUp}
          disabled={!enablePickup}
          onSelect={() => setIsPickUp(true)}
          icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 26 }} />}
          title="Pickup"
          caption={
            !enablePickup
              ? "Not offered here"
              : catering
                ? "No delivery fee"
                : `Ready in ~${prepTime} min · no delivery fee`
          }
        />
      </RadioGroup>

      {addressCard && <Box sx={{ mt: 2 }}>{addressCard}</Box>}

      <Box sx={{ mt: 3 }}>
        <Box id="checkout-when-label" sx={{ ...TYPE.subHeading, color: C.ink, mb: 1.25 }}>When</Box>
        <RadioGroup labelledBy="checkout-when-label" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: catering ? "1fr" : "1fr 1fr" }, gap: 1.5 }}>
          {/* No ASAP for catering — it is always ordered ahead. */}
          {!catering && (
            <OptionCard
              checked={isAsap && asapAvailable}
              disabled={!asapAvailable}
              onSelect={chooseAsap}
              icon={<BoltRoundedIcon sx={{ fontSize: 24 }} />}
              title={isPickUp ? "Pick up now (ASAP)" : "Deliver now (ASAP)"}
              caption={
                !asapAvailable
                  ? "Not available right now — closed"
                  : isPickUp
                    ? `Ready in ~${prepTime} min`
                    : "As soon as possible"
              }
              captionColor={!asapAvailable ? C.redLink : undefined}
            />
          )}
          <OptionCard
            checked={!isAsap}
            onSelect={chooseSchedule}
            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 24 }} />}
            title="Schedule for later"
            caption={scheduleCaption}
          />
        </RadioGroup>

        {showSchedule && !isAsap && (
          <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <SelectField id="checkout-date" label="Date" value={String(selectedDay.getTime())} onChange={chooseDay}>
              {days.map((day) => (
                <option key={day.getTime()} value={String(day.getTime())}>
                  {dayLabel(day, tz)}
                </option>
              ))}
            </SelectField>
            <SelectField
              id="checkout-time"
              label="Time"
              value={timeValue}
              disabled={timeSlots.length === 0 && !offGrid}
              onChange={(v) => chooseSlot(new Date(Number(v)))}
            >
              {timeSlots.length === 0 && !offGrid ? (
                <option value="">No times this day</option>
              ) : (
                <>
                  {!timeValue && <option value="" disabled>Choose a time</option>}
                  {offGrid && <option value={String(selectedTs)}>{timeIn(selectedTs, tz)}</option>}
                  {timeSlots.map((slot) => (
                    <option key={slot.getTime()} value={String(slot.getTime())}>
                      {timeIn(slot, tz)}
                    </option>
                  ))}
                </>
              )}
            </SelectField>
          </Box>
        )}
        {showSchedule && !isAsap && timeSlots.length === 0 && !offGrid && (
          <Box sx={{ ...metaSx, mt: 1 }}>The restaurant is closed this day. Choose another date.</Box>
        )}
      </Box>

      {children}
    </Box>
  );
}

export default React.memo(OrderOptions);
